/* global globalThis */
import React from "react";
import PropTypes from "prop-types";
import { invalidateSessionAndRedirect } from "./InvalidateSession";
import {
  Dialog,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
} from "@mui/material";
import styles from "../shared/globalNavBar/GlobalNavBar.module.scss";
import { withTranslation } from "react-i18next";
import AuthenticationClient from "../clients/AuthenticationClient";
import { is401 } from "../errors/ErrorConstants";

export default function withIdleTimer(Component) {
  const DIALOG_OPEN_TIMEOUT = 10000 * 84;
  const WARNING_COUNTDOWN_SECONDS = 60;

  return withTranslation("emp")(
    class Timer extends React.Component {
      static propTypes = {
        t: PropTypes.func.isRequired,
      };

      constructor(props) {
        super(props);

        this.state = {
          open: false,
          dialogueCountdown: WARNING_COUNTDOWN_SECONDS,
        };

        this.countDown = this.countDown.bind(this);
      }

      componentDidMount() {
        this.events = [
          "load",
          "mousemove",
          "mousedown",
          "click",
          "scroll",
          "keypress",
        ];

        for (const i in this.events) {
          globalThis.addEventListener(this.events[i], this.resetTimeout);
        }

        this.setTimeout();
      }

      handleLogoutDialogOpen = async () => {
        try {
          // Ping the backend with the current MSAL-issued token via the
          // axios interceptor. A 401 here means the silent token refresh
          // failed (e.g. the Entra session ended) — fall straight through
          // to a forced re-auth. Any other failure (network, 5xx) just
          // shows the standard idle warning.
          await AuthenticationClient.health();

          this.showDialogAndStartTimer();
        } catch (error) {
          if (is401(error)) {
            invalidateSessionAndRedirect();
          } else {
            this.showDialogAndStartTimer();
          }
        }
      };

      handleLogoutDialogClose = () => {
        clearInterval(this.timer);
        this.setState({
          open: false,
          dialogueCountdown: WARNING_COUNTDOWN_SECONDS,
        });
      };

      resetTimeout = () => {
        this.warnTimeout && clearTimeout(this.warnTimeout);
        this.warnTimeout = setTimeout(
          this.handleLogoutDialogOpen,
          DIALOG_OPEN_TIMEOUT
        );
      };

      setTimeout = () =>
        (this.warnTimeout = setTimeout(
          this.handleLogoutDialogOpen,
          DIALOG_OPEN_TIMEOUT
        ));

      showDialogAndStartTimer = () => {
        this.setState({ open: true });
        this.startTimer();
      };

      startTimer() {
        if (this.state.dialogueCountdown > 0) {
          this.timer = setInterval(this.countDown, 1000);
        }
      }

      countDown() {
        this.setState(
          (prevState) => ({
            dialogueCountdown: prevState.dialogueCountdown - 1,
          }),
          () => {
            if (this.state.dialogueCountdown === 0) {
              clearInterval(this.timer);
              invalidateSessionAndRedirect();
            }
          }
        );
      }

      render() {
        const dialogText = this.props.t("Logout.confirmStaySignedIn");
        const seconds =
          this.state.dialogueCountdown > 60
            ? "60"
            : this.state.dialogueCountdown !== 0 &&
              this.state.dialogueCountdown;

        return (
          <div>
            <Dialog
              open={this.state.open}
              onClose={this.handleLogoutDialogClose}
              aria-labelledby="alert-dialog-title"
              aria-describedby="alert-dialog-description"
            >
              <DialogContent>
                <DialogContentText id="alert-dialog-description">
                  <span className={styles.logoutConfirmationFont}>
                    {dialogText.replace("%s", seconds)}
                  </span>
                </DialogContentText>
              </DialogContent>
              <DialogActions>
                <Button onClick={this.handleLogoutDialogClose} color="primary">
                  {this.props.t("Logout.staySignedIn")}
                </Button>
              </DialogActions>
            </Dialog>
            <Component {...this.props} />
          </div>
        );
      }
    }
  );
}
